import React, { useCallback, useEffect, useState } from 'react';
import { OverrideReturn, ScheduleReturn } from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Button } from '../common/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../common/Catalyst/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '../common/Catalyst/fieldset';
import { Input } from '../common/Catalyst/input';
import { FieldPicker } from '../common/Custom/FieldPicker';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../common/Catalyst/table';
import { Text } from '../common/Catalyst/text';
import { Subheading } from '../common/Catalyst/heading';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import {
  billingErrorMessage,
  formatBillingDate,
  formatMoney,
} from '../utils/billingHelpers';
import { TextSetting } from './BillingPolicyFields';

interface Props {
  hoaId: string;
  homeId: string;
  canManage: boolean;
}

const MONEY = /^\d+(\.\d{1,2})?$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A home's own dues rate, as a series of dated changes.
 *
 * **An override is a statement about a date range, not a revision of
 * the row before it.** Resolution is effective-dating — the row with
 * the latest `effective_from` that is in force on the period wins — so
 * "change this home's dues from June" is *adding a row*, and the old
 * one stays as the record of what was actually charged before. That is
 * what makes re-running an old period reproduce the old number.
 *
 * A row that has already billed is immutable, and the backend says so
 * with a 409 that names the alternative. This surfaces that message
 * rather than second-guessing which rows are safe.
 */
const HomeOverrideList: React.FC<Props> = ({ hoaId, homeId, canManage }) => {
  const [overrides, setOverrides] = useState<OverrideReturn[] | null>(null);
  const [schedules, setSchedules] = useState<ScheduleReturn[]>([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<{
    type: 'success' | 'error';
    title: string;
    message: string;
  } | null>(null);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<OverrideReturn | null>(null);
  const [scheduleId, setScheduleId] = useState('');
  const [amount, setAmount] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [o, s] = await Promise.all([
        billingService.listOverrides({ homeId }),
        billingService.listSchedules({ hoaId }),
      ]);
      setOverrides(o.data.overrides);
      setSchedules(s.data.schedules);
    } catch (err) {
      console.error('Error loading overrides:', err);
      setAlert({
        type: 'error',
        title: "Couldn't load this home’s dues",
        message: billingErrorMessage(err),
      });
    } finally {
      setLoading(false);
    }
  }, [hoaId, homeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const scheduleName = (id: string): string =>
    schedules.find((s) => s.id === id)?.name ?? id;

  const openNew = (): void => {
    const preferred =
      schedules.find((s) => s.status === 'active') ?? schedules[0];
    setEditing(null);
    setScheduleId(preferred?.id ?? '');
    setAmount('');
    setFrom('');
    setTo('');
    setErrors({});
    setOpen(true);
  };

  const openEdit = (o: OverrideReturn): void => {
    setEditing(o);
    setScheduleId(o.schedule_id);
    setAmount(o.amount);
    setFrom(o.effective_from ?? '');
    setTo(o.effective_to ?? '');
    setErrors({});
    setOpen(true);
  };

  const save = async (): Promise<void> => {
    const found: Record<string, string> = {};
    if (!MONEY.test(amount.trim())) {
      found.amount = 'Enter an amount like 275 or 275.00.';
    }
    if (!editing && !scheduleId) {
      found.scheduleId = 'Pick the schedule this rate replaces.';
    }
    if (from && !DATE.test(from)) found.from = 'Use a date, or leave blank.';
    if (to && !DATE.test(to)) found.to = 'Use a date, or leave blank.';
    if (from && to && from > to) {
      found.to = 'The end date is before the start date.';
    }
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    try {
      setSaving(true);
      if (editing) {
        await billingService.updateOverride({
          overrideId: editing.id,
          overrideUpdate: {
            amount: amount.trim(),
            effective_from: from || null,
            effective_to: to || null,
          },
        });
      } else {
        await billingService.createOverride({
          homeId,
          overrideCreate: {
            schedule_id: scheduleId,
            amount: amount.trim(),
            effective_from: from || null,
            effective_to: to || null,
          },
        });
      }
      setOpen(false);
      setAlert({
        type: 'success',
        title: editing ? 'Dues change updated' : 'Dues change added',
        message: editing
          ? 'The correction is saved.'
          : 'It applies from its start date onward, and supersedes any earlier rate.',
      });
      await load();
    } catch (err) {
      console.error('Error saving override:', err);
      // A 409 here is the effective-dating guard, and its message names
      // the way forward. Show it as written.
      setErrors({ form: billingErrorMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (o: OverrideReturn): Promise<void> => {
    try {
      await billingService.deleteOverride({ overrideId: o.id });
      setAlert({
        type: 'success',
        title: 'Dues change removed',
        message: 'This home is back on the schedule’s standard amount.',
      });
      await load();
    } catch (err) {
      console.error('Error deleting override:', err);
      setAlert({
        type: 'error',
        title: 'This one cannot be removed',
        message: billingErrorMessage(err),
      });
    }
  };

  if (loading) return <LoadingSpinner />;

  const rows = overrides ?? [];

  return (
    <>
      {alert && (
        <div className="mb-4">
          <ShowAlert
            alertType={alert.type}
            alertTitle={alert.title}
            alertMessage={alert.message}
            isVisible={!!alert}
            onClose={() => setAlert(null)}
            timeout={alert.type === 'success' ? 6000 : 14000}
            isInline
          />
        </div>
      )}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-4">
        <div>
          <Subheading>Dues for this home</Subheading>
          <Text className="mt-1">
            What this home pays, when it differs from the association&rsquo;s
            standard amount. Each row is a rate applying from a date — to change
            the rate, add a new row rather than editing an old one, so what was
            already charged stays true.
          </Text>
        </div>
        {canManage && schedules.length > 0 && (
          <Button color="sky" onClick={openNew}>
            Add a dues change
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 p-6">
          <Text>This home pays the association&rsquo;s standard amount.</Text>
        </div>
      ) : (
        <Table dense>
          <TableHead>
            <TableRow>
              <TableHeader>Amount</TableHeader>
              <TableHeader>From</TableHeader>
              <TableHeader>Until</TableHeader>
              <TableHeader>Instead of</TableHeader>
              {canManage && <TableHeader className="text-right"> </TableHeader>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((o) => (
              <TableRow key={o.id}>
                <TableCell className="font-medium">
                  {formatMoney(o.amount)}
                </TableCell>
                <TableCell>
                  {o.effective_from
                    ? formatBillingDate(o.effective_from)
                    : 'Always'}
                </TableCell>
                <TableCell>
                  {o.effective_to ? formatBillingDate(o.effective_to) : '—'}
                </TableCell>
                <TableCell>{scheduleName(o.schedule_id)}</TableCell>
                {canManage && (
                  <TableCell className="text-right">
                    <Button plain onClick={() => openEdit(o)}>
                      Correct
                    </Button>
                    <Button plain onClick={() => remove(o)}>
                      Remove
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} size="xl">
        <DialogTitle>
          {editing ? 'Correct a dues change' : 'Add a dues change'}
        </DialogTitle>
        <DialogBody>
          {errors.form && (
            <div className="mb-4">
              <ShowAlert
                alertType="error"
                alertTitle="This change cannot be made"
                alertMessage={errors.form}
                isVisible
                onClose={() => setErrors((e) => ({ ...e, form: '' }))}
                timeout={0}
                isInline
              />
            </div>
          )}
          <Fieldset>
            <FieldGroup>
              {!editing && schedules.length > 1 && (
                <Field>
                  <Label>Instead of</Label>
                  <Description>
                    Which schedule&rsquo;s amount this rate replaces.
                  </Description>
                  <FieldPicker
                    label="Instead of"
                    value={scheduleId}
                    onChange={setScheduleId}
                    options={schedules.map((s) => ({
                      value: s.id,
                      label: `${s.name} (${formatMoney(s.amount)})`,
                    }))}
                  />
                  {errors.scheduleId && (
                    <ErrorMessage>{errors.scheduleId}</ErrorMessage>
                  )}
                </Field>
              )}

              <TextSetting
                label="This home pays"
                prefix="$"
                value={amount}
                error={errors.amount}
                onChange={setAmount}
              />

              <Field>
                <Label>From</Label>
                <Description>
                  The first period this rate applies to. Blank means it has
                  always applied.
                </Description>
                <Input
                  type="date"
                  value={from}
                  invalid={!!errors.from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="max-w-[14rem]"
                />
                {errors.from && <ErrorMessage>{errors.from}</ErrorMessage>}
              </Field>

              <Field>
                <Label>Until</Label>
                <Description>
                  Blank means it applies until something later supersedes it.
                </Description>
                <Input
                  type="date"
                  value={to}
                  invalid={!!errors.to}
                  onChange={(e) => setTo(e.target.value)}
                  className="max-w-[14rem]"
                />
                {errors.to && <ErrorMessage>{errors.to}</ErrorMessage>}
              </Field>

              {editing && (
                <Text>
                  Correcting is for a row that has not billed anything yet. If
                  it has, this is refused and the message will say so — add a
                  new row from the date the new rate starts instead.
                </Text>
              )}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button color="sky" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Save correction' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HomeOverrideList;
